import React from 'react';
import { Flex, Text, Tooltip } from '@fullstory/components';
import { TooltipRow } from '../TooltipRow';
import { useSingleNumberComparison } from '../../hooks';
import trendTextColors from './index.module.scss';
import { Display } from '@fullstory/proto/fs/services/webber/frontend/display';
import { TimeRange } from '@fullstory/proto/fs/api/segment';
import {
  Color,
  TimeRangeComparison,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { getColorGroup, getPercentChangeText } from './utils';

interface SingleNumberDXComparison {
  value: number;
  pastValue: number;
  displayFormat: Display | undefined;
  customFormatter?: (value: number | null) => string;
  timeRange: TimeRange | null | undefined;
  pastRange: TimeRangeComparison | null | undefined;
  color: Color;
  small?: boolean;
}

export const SingleNumberDXComparison: React.FC<SingleNumberDXComparison> = ({
  value,
  pastValue,
  timeRange,
  pastRange,
  displayFormat,
  color,
  small = false,
  customFormatter,
}) => {
  const { previousRange, currentRange, comparisonText, comparisonValue, valueText, pastValueText } =
    useSingleNumberComparison({
      value,
      pastValue,
      timeRange,
      pastRange,
      displayFormat,
      customFormatter,
      abs: false,
      formatNumberOptions: { signDisplay: 'always' },
    });
  const colorMapping = getColorGroup(color);

  const isSameValue = value === pastValue;
  const isIncreasing = value > pastValue;
  const percentageChangeColor = isIncreasing
    ? colorMapping.positive.fontColor
    : !isFinite(comparisonValue) || isSameValue
    ? colorMapping.neutral.fontColor
    : colorMapping.negative.fontColor;

  const hasContent = !!comparisonText && !!previousRange;

  return (
    <Tooltip
      data-component="SingleNumberDXComparison"
      tooltipContent={
        hasContent && (
          <>
            <TooltipRow rangeText={currentRange} value={valueText} />
            <TooltipRow rangeText={previousRange} value={pastValueText} />
          </>
        )
      }
      disabled={!hasContent}
      placement="bottom"
    >
      <Flex className={trendTextColors[percentageChangeColor]}>
        <Text appearance={small ? 'bodyXSmall' : 'bodySmall'} color="inherit">
          {`${getPercentChangeText(isSameValue, comparisonValue, comparisonText)}`}
        </Text>
      </Flex>
    </Tooltip>
  );
};
